import React, { useCallback, useEffect, useState } from 'react';
import { Activity, AlertTriangle, CircleOff, HelpCircle, Radio, Timer, Zap, RefreshCw, History, X } from 'lucide-react';
import { toast } from 'sonner';
import { callingAPI } from '../api';
import { useAuth } from '../context/AuthContext';

const STATE_META = {
  HEALTHY: { label: 'Healthy', dot: 'bg-emerald-500', chip: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-300' },
  DEGRADED: { label: 'Degraded', dot: 'bg-amber-500', chip: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-300' },
  OFFLINE: { label: 'Offline', dot: 'bg-rose-500', chip: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/20 dark:text-rose-300' },
  NEVER_TESTED: { label: 'Never Tested', dot: 'bg-slate-500', chip: 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300' },
};

// Timestamps are optional everywhere in the health payload: absent/null -> "Never",
// present but unparsable -> "Unavailable". Never render "Invalid Date".
const fmt = (ts) => {
  if (ts === null || ts === undefined || ts === '') return 'Never';
  const raw = typeof ts === 'string' ? (ts.endsWith('Z') || /[+-]\d{2}:?\d{2}$/.test(ts) ? ts : `${ts}Z`) : ts;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return 'Unavailable';
  return d.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit' });
};
const ms = (v) => (v == null ? '—' : `${(v / 1000).toFixed(2)} s`);
// Split only a real formatted timestamp; the Never/Unavailable sentinels pass through untouched.
const fmtTime = (ts) => { const s = fmt(ts); return s.includes(', ') ? s.split(', ')[1] : s; };
const fmtDay = (ts) => { const s = fmt(ts); return s.includes(', ') ? s.split(', ')[0] : (ts ? s : ''); };

const Card = ({ label, value, sub, icon: Icon, active, onClick, testId, tone = '' }) => (
  <button data-testid={testId} onClick={onClick}
    className={`group flex flex-col items-start gap-1 rounded-xl border px-4 py-3 text-left transition-colors hover:border-primary/60 hover:bg-primary/5 ${active ? 'border-primary bg-primary/5' : 'border-slate-200 dark:border-slate-700'}`}>
    <span className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
      {Icon && <Icon className="h-3.5 w-3.5" />}{label}
    </span>
    <span className={`text-2xl font-semibold leading-none ${tone}`}>{value}</span>
    {sub && <span className="text-[11px] text-muted-foreground">{sub}</span>}
  </button>
);

const Field = ({ label, value, testId }) => (
  <div data-testid={testId}>
    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className="text-sm font-medium break-words">{value ?? '—'}</p>
  </div>
);

export const CallingSystemHealth = () => {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [devices, setDevices] = useState([]);
  const [filter, setFilter] = useState('ALL');
  const [detail, setDetail] = useState(null);
  const [tests, setTests] = useState([]);
  const [testing, setTesting] = useState(null);
  const [result, setResult] = useState(null);
  const perms = user?.permissions || [];
  const canTest = user?.role === 'super_admin' || perms.includes('calling_health.test_connection') || perms.includes('telephony.test_connection');
  const canDiag = user?.role === 'super_admin' || perms.includes('calling_health.view_diagnostics') || perms.includes('telephony.health.view');

  const load = useCallback(async (state = 'ALL', pendingOnly = false) => {
    try {
      const [s, d] = await Promise.all([callingAPI.healthSummary(), callingAPI.healthDevices({ state, pending_only: pendingOnly })]);
      setSummary(s.data); setDevices(d.data.devices || []);
    } catch (e) { /* permission-gated section */ }
  }, []);

  useEffect(() => { load('ALL'); }, [load]);

  const applyFilter = (state, pendingOnly = false) => { setFilter(state); load(state, pendingOnly); };

  const openDetail = async (deviceId) => {
    const [d, t] = await Promise.all([
      callingAPI.healthDevice(deviceId),
      canDiag ? callingAPI.connectionTests(deviceId).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
    ]);
    setDetail(d.data); setTests(t.data || []);
  };

  const runTest = async (deviceId) => {
    setTesting(deviceId); setResult(null);
    try {
      const r = await callingAPI.testConnection(deviceId);
      setResult(r.data);
      if (r.data.result === 'PASSED') toast.success(r.data.message);
      else toast.error(r.data.message);
      await load(filter);
      if (detail?.device_id === deviceId) await openDetail(deviceId);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Connection test could not be started');
    } finally { setTesting(null); }
  };

  if (!summary) return null;

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-700 dark:bg-slate-900" data-testid="calling-system-health">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Calling System Health</h2>
          <p className="text-xs text-muted-foreground">Real delivery evidence only — an FCM token alone is never counted as healthy.</p>
          {summary.effective_scope_label && <span className="mt-1 inline-block rounded-full border border-primary/40 bg-primary/5 px-2 py-0.5 text-[11px]" data-testid="health-scope-badge">Viewing: {summary.effective_scope_label}</span>}
        </div>
        <button data-testid="health-refresh-btn" onClick={() => load(filter)} className="inline-flex items-center gap-1 rounded-lg border px-3 py-1.5 text-xs">
          <RefreshCw className="h-3.5 w-3.5" />Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7">
        <Card testId="health-card-healthy" label="Healthy" value={summary.healthy} icon={Activity} tone="text-emerald-600" active={filter === 'HEALTHY'} onClick={() => applyFilter('HEALTHY')} />
        <Card testId="health-card-degraded" label="Degraded" value={summary.degraded} icon={AlertTriangle} tone="text-amber-600" active={filter === 'DEGRADED'} onClick={() => applyFilter('DEGRADED')} />
        <Card testId="health-card-offline" label="Offline" value={summary.offline} icon={CircleOff} tone="text-rose-600" active={filter === 'OFFLINE'} onClick={() => applyFilter('OFFLINE')} />
        <Card testId="health-card-never-tested" label="Never Tested" value={summary.never_tested} icon={HelpCircle} active={filter === 'NEVER_TESTED'} onClick={() => applyFilter('NEVER_TESTED')} />
        <Card testId="health-card-pending" label="Pending Commands" value={summary.pending_commands} icon={Radio} active={filter === 'PENDING'} onClick={() => applyFilter('ALL', true)} />
        <Card testId="health-card-last-delivery" label="Last FCM Delivery" value={fmtTime(summary.last_successful_fcm_delivery_at)} sub={fmtDay(summary.last_successful_fcm_delivery_at) || 'no delivery yet'} icon={Zap} onClick={() => applyFilter('HEALTHY')} />
        <Card testId="health-card-avg-latency" label="Avg FCM/ACK Latency" value={ms(summary.recent_avg_latency_ms)} sub={`last ${summary.recent_latency_sample} deliveries`} icon={Timer} onClick={() => applyFilter('ALL')} />
      </div>

      {summary.degraded_alerts > 0 && canDiag && (
        <div className="mt-3 rounded-xl border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800 dark:bg-amber-900/20 dark:text-amber-300" data-testid="fcm-degraded-banner">
          <AlertTriangle className="mr-1 inline h-3.5 w-3.5" />
          FCM instant wake was not confirmed on {summary.degraded_alerts} device(s). Command recovered via polling — calls still went through.
        </div>
      )}

      <div className="mt-4 flex items-center gap-2 text-xs">
        <span className="text-muted-foreground">Showing:</span>
        <span className="rounded-full border px-2 py-0.5" data-testid="health-active-filter">{filter === 'ALL' ? 'All devices' : STATE_META[filter]?.label || filter}</span>
        {filter !== 'ALL' && <button data-testid="health-clear-filter" onClick={() => applyFilter('ALL')} className="text-primary underline">Clear filter</button>}
        <span className="ml-auto text-muted-foreground" data-testid="health-device-count">{devices.length} device(s)</span>
      </div>

      <div className="mt-3 space-y-2">
        {devices.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground" data-testid="health-empty">No devices in this view.</p>}
        {devices.map((d) => {
          const meta = STATE_META[d.health_state] || STATE_META.NEVER_TESTED;
          return (
            <div key={d.device_id} data-testid={`health-device-${d.device_id}`} className="rounded-xl border p-3 dark:border-slate-700">
              <div className="flex flex-wrap items-center gap-3">
                <span className={`h-2.5 w-2.5 rounded-full ${meta.dot}`} />
                <button onClick={() => openDetail(d.device_id)} className="text-sm font-semibold hover:underline" data-testid={`health-device-open-${d.device_id}`}>
                  {d.user?.name || 'Unassigned'} · {d.device_model || 'Device'}
                </button>
                <span className={`rounded-full border px-2 py-0.5 text-[11px] ${meta.chip}`} data-testid={`health-state-${d.device_id}`}>{meta.label}</span>
                <span className="text-xs text-muted-foreground" data-testid={`health-seen-${d.device_id}`}>{d.last_seen_human}</span>
                <span className="text-xs text-muted-foreground">Latency {ms(d.last_command_latency_ms)}</span>
                <span className="text-xs text-muted-foreground">Pending {d.pending_commands}</span>
                <span className="text-xs text-muted-foreground" data-testid={`health-delivery-${d.device_id}`}>
                  Delivery {d.last_delivery_method === 'INSTANT_FCM' ? 'Instant FCM' : d.last_delivery_method === 'POLL_FALLBACK' ? 'Poll Fallback' : 'Unknown'}
                </span>
                {canTest && d.enrollment_status === 'active' && (
                  <button data-testid={`test-connection-btn-${d.device_id}`} disabled={testing === d.device_id}
                    onClick={() => runTest(d.device_id)}
                    className="ml-auto inline-flex items-center gap-1 rounded-lg bg-primary px-3 py-1.5 text-xs text-white disabled:opacity-60">
                    <Zap className="h-3.5 w-3.5" />{testing === d.device_id ? 'Testing…' : 'Test Connection'}
                  </button>
                )}
              </div>
              <p className="mt-1 text-[11px] text-muted-foreground" data-testid={`health-reason-${d.device_id}`}>{d.health_reason}</p>
              {d.fcm_degraded_alert && canDiag && (
                <p className="mt-1 text-[11px] font-medium text-amber-700 dark:text-amber-400" data-testid={`health-degraded-${d.device_id}`}>
                  FCM instant wake was not confirmed. Command recovered via polling.
                </p>
              )}
              {result && result.device_id === undefined && testing === null && detail === null && null}
            </div>
          );
        })}
      </div>

      {result && (
        <div data-testid="test-result-banner"
          className={`mt-4 rounded-xl border p-3 text-sm ${result.result === 'PASSED' ? 'border-emerald-300 bg-emerald-50 text-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-300' : 'border-rose-300 bg-rose-50 text-rose-800 dark:bg-rose-900/20 dark:text-rose-300'}`}>
          <p className="font-semibold" data-testid="test-result-message">{result.message}</p>
          <p className="mt-1 text-xs">
            FCM: {result.fcm_status || '—'} · accepted {fmt(result.fcm_accepted_at)} · ACK {fmt(result.ack_at)} ({result.ack_source || 'none'}) · round-trip {ms(result.round_trip_ms)}
            {result.failure_stage ? ` · stage ${result.failure_stage}` : ''}
          </p>
          <p className="mt-1 break-all text-[11px] opacity-80">token {result.token_fingerprint} · message id {result.fcm_message_id || '—'}</p>
        </div>
      )}

      {detail && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4" data-testid="device-health-detail">
          <div className="mt-8 w-full max-w-3xl rounded-2xl bg-white p-5 dark:bg-slate-900">
            <div className="mb-4 flex items-start justify-between">
              <div>
                <h3 className="text-base font-semibold">{detail.user?.name} · {detail.device_model}</h3>
                <p className="text-xs text-muted-foreground">{detail.user?.employee} · device {detail.device_fingerprint}</p>
              </div>
              <button data-testid="device-health-close" onClick={() => { setDetail(null); setTests([]); }} className="rounded p-1 hover:bg-slate-100 dark:hover:bg-slate-800"><X className="h-4 w-4" /></button>
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <Field testId="detail-state" label="Current Health State" value={STATE_META[detail.health_state]?.label} />
              <Field testId="detail-app-version" label="App Version" value={detail.app_version} />
              <Field testId="detail-environment" label="Environment" value={detail.environment} />
              <Field testId="detail-enrollment" label="Enrollment" value={`${detail.enrollment_status}${detail.is_default ? ' · default' : ''}`} />
              <Field testId="detail-fcm" label="FCM Registration" value={detail.fcm_registered ? `registered (${detail.fcm_token_fingerprint})` : 'not registered'} />
              <Field testId="detail-heartbeat" label="API / Heartbeat" value={`${detail.heartbeat_ok ? 'connected' : 'stale'} · ${detail.last_seen_human}`} />
              <Field testId="detail-last-send" label="Last FCM Send" value={fmt(detail.last_fcm_send_at)} />
              <Field testId="detail-last-accepted" label="Last Firebase Accepted" value={fmt(detail.last_firebase_accepted_at)} />
              <Field testId="detail-last-ack" label="Last DEVICE_RECEIVED ACK" value={fmt(detail.last_device_ack_at)} />
              <Field testId="detail-last-c2c" label="Last Successful Click-to-Call" value={fmt(detail.last_successful_click_to_call_at)} />
              <Field testId="detail-latency" label="Last Command Latency" value={ms(detail.last_command_latency_ms)} />
              <Field testId="detail-pending" label="Pending Commands" value={detail.pending_commands} />
              <Field testId="detail-sync" label="Last Synchronization" value={fmt(detail.last_sync_at)} />
              <Field testId="detail-crm-api" label="CRM / API" value={detail.crm_api_state} />
              <Field testId="detail-delivery-method" label="Last Delivery Method" value={detail.last_delivery_method === 'INSTANT_FCM' ? 'Instant FCM' : detail.last_delivery_method === 'POLL_FALLBACK' ? 'Poll Fallback' : 'Unknown'} />
              <Field testId="detail-token-updated" label="FCM Token Updated" value={fmt(detail.fcm_token_updated_at)} />
              <Field testId="detail-reason" label="Why this state" value={detail.health_reason} />
            </div>
            {canDiag && (
            <div className="mt-5">
              <p className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground"><History className="h-3.5 w-3.5" />Connection test history</p>
              {tests.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="test-history-empty">No connection tests recorded yet.</p> : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs" data-testid="test-history-table">
                    <thead className="text-muted-foreground">
                      <tr><th className="py-1 pr-3">Time</th><th className="pr-3">By</th><th className="pr-3">FCM</th><th className="pr-3">Accepted</th><th className="pr-3">Device ACK</th><th className="pr-3">Round-trip</th><th className="pr-3">Result</th><th>Stage</th></tr>
                    </thead>
                    <tbody>
                      {tests.map((t) => (
                        <tr key={t.id} className="border-t dark:border-slate-700" data-testid={`test-row-${t.id}`}>
                          <td className="py-1.5 pr-3">{fmt(t.started_at)}</td>
                          <td className="pr-3">{t.initiated_by_name || '—'}</td>
                          <td className="pr-3">{t.fcm_status || '—'}</td>
                          <td className="pr-3">{fmt(t.fcm_accepted_at)}</td>
                          <td className="pr-3">{fmt(t.ack_at)}</td>
                          <td className="pr-3">{ms(t.round_trip_ms)}</td>
                          <td className={`pr-3 font-medium ${t.result === 'PASSED' ? 'text-emerald-600' : 'text-rose-600'}`}>{t.result}</td>
                          <td>{t.failure_stage || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>)}
            {canTest && detail.enrollment_status === 'active' && (
              <button data-testid="detail-test-connection-btn" disabled={testing === detail.device_id}
                onClick={() => runTest(detail.device_id)}
                className="mt-4 inline-flex items-center gap-1 rounded-lg bg-primary px-4 py-2 text-sm text-white disabled:opacity-60">
                <Zap className="h-4 w-4" />{testing === detail.device_id ? 'Testing…' : 'Test Connection'}
              </button>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

export default CallingSystemHealth;
